import type { BridgeFetchOptions, BridgeFetchResponse } from "../bridge/ExtensionBridgeClient.ts";
import {
  getAuthSessionKey,
  getSourceCacheScope,
  hasSourceSession,
} from "./SourceSession.ts";
import { YomiriHttpClient } from "../network/YomiriHttpClient.ts";
import { TransportRouter } from "../network/TransportRouter.ts";
import { DirectTransport } from "../network/transports/DirectTransport.ts";
import { ExtensionTransport } from "../network/transports/ExtensionTransport.ts";
import { PersonalProxyTransport } from "../network/transports/PersonalProxyTransport.ts";
import { getNetworkSettings } from "../network/NetworkSettings.ts";
import { sourceIdForUrl } from "../network/SourceRegistry.ts";
import {
  NetworkError,
  type HttpRequest,
  type HttpResponse,
  type PreparedImage,
} from "../network/types.ts";

export const sourceExtensionTransport = new ExtensionTransport();
export const sourceHttpClient = new YomiriHttpClient({
  sessionKey: getSourceCacheScope,
  router: new TransportRouter({
    direct: new DirectTransport(),
    extension: sourceExtensionTransport,
    personalProxy: new PersonalProxyTransport(() => getNetworkSettings().personalProxy),
  }),
});

if (typeof window !== "undefined") {
  window.addEventListener("yomiri-transport-changed", () => sourceHttpClient.clearCache());
  window.addEventListener("storage", (event) => {
    if (!event.key || event.key === "yomiri_network_settings") sourceHttpClient.clearCache();
  });
}

export interface SourceFetchOptions extends BridgeFetchOptions {
  sourceId?: string;
  corsOpen?: boolean;
  requiresSession?: boolean;
  purpose?: "metadata" | "image";
  cacheTtlMs?: number;
  signal?: AbortSignal;
}

function makeRequest(
  url: string,
  options: SourceFetchOptions,
  responseType: "text" | "bytes",
): HttpRequest {
  const method = options.method ?? "GET";
  if (method !== "GET" && method !== "POST")
    throw new NetworkError("INVALID_RESPONSE", "Este transporte admite GET y POST.");
  return {
    sourceId: options.sourceId ?? sourceIdForUrl(url),
    url,
    method,
    headers: options.headers,
    body: options.body,
    credentials: options.credentials ?? "omit",
    responseType,
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    corsOpen: options.corsOpen,
    requiresSession: options.requiresSession,
    purpose: options.purpose ?? "metadata",
    cache: {
      ttlMs: options.cacheTtlMs ?? (options.purpose === "image" ? 0 : 5 * 60000),
      scope:
        options.credentials === "include" || Object.keys(options.headers ?? {}).length
          ? "session"
          : "public",
    },
  };
}

function asResponse(response: HttpResponse<Uint8Array>): Response {
  return new Response(
    [204, 205, 304].includes(response.status)
      ? null
      : (response.body.slice().buffer as ArrayBuffer),
    { status: response.status, headers: response.headers },
  );
}

// Kept for existing imports. Uses the cost-first router instead of forcing server traffic.
export function sourceWorkerFetch(
  url: string,
  options: SourceFetchOptions = {},
): Promise<Response> {
  return fetchSourceResponse(url, options);
}

export async function fetchSourceResponse(
  url: string,
  options: SourceFetchOptions = {},
): Promise<Response> {
  const request = makeRequest(url, options, "bytes");
  return asResponse(await sourceHttpClient.bytes({ ...request, responseType: "bytes" }));
}

export async function fetchSourceBridge(
  url: string,
  options: SourceFetchOptions = {},
): Promise<BridgeFetchResponse> {
  const binary = options.responseType === "base64" || options.responseType === "blob";
  const request = makeRequest(url, options, binary ? "bytes" : "text");
  const response = binary
    ? await sourceHttpClient.bytes({ ...request, responseType: "bytes" })
    : await sourceHttpClient.text({ ...request, responseType: "text" });
  let data: unknown;
  if (response.body instanceof Uint8Array) {
    const chunks: string[] = [];
    for (let offset = 0; offset < response.body.length; offset += 32768)
      chunks.push(String.fromCharCode(...response.body.subarray(offset, offset + 32768)));
    data = `data:${response.headers["content-type"] ?? "application/octet-stream"};base64,${btoa(chunks.join(""))}`;
  } else data = options.responseType === "json" ? JSON.parse(response.body) : response.body;
  return {
    ok: true,
    status: response.status,
    statusText: "",
    headers: response.headers,
    data,
    transport: response.transport,
  };
}

export function prepareSourceImage(
  url: string,
  headers?: Record<string, string>,
  sourceId?: string,
  signal?: AbortSignal,
): Promise<PreparedImage> {
  if (url.startsWith("blob:") || url.startsWith("data:image/"))
    return Promise.resolve({ url, delivery: "direct", release() {} });
  const referer = Object.entries(headers ?? {}).find(
    ([name]) => name.toLowerCase() === "referer",
  )?.[1];
  return sourceHttpClient.prepareImage({
    signal,
    sourceId: sourceId ?? sourceIdForUrl(url),
    url,
    headers,
    referer,
  });
}
